"use strict";

class NavigationDropdown {
  constructor(domNode) {
    this.rootEl = domNode;

    this.buttonEl = this.rootEl.querySelector('[aria-haspopup="menu"]');
    const controlsId = this.buttonEl.getAttribute("aria-controls");
    this.contentEl = document.getElementById(controlsId);

    if (!this.buttonEl || !this.contentEl) {
      throw new Error(
        'NavigationDropdown: button with aria-haspopup="menu" and menu with role="menu" are required'
      );
    }

    this.menuItems = this.contentEl.querySelectorAll('[role="menuitem"]');

    // State management
    this.currentFocusIndex = -1;
    this.isOpen = this.buttonEl.getAttribute("aria-expanded") === "true";

    this.bindEvents();

    // Set initial state: default to closed unless explicitly set to stay open
    const shouldStayOpen = this.rootEl.hasAttribute("data-initially-open");
    this.toggle(shouldStayOpen);
  }

  bindEvents() {
    this.buttonEl.addEventListener("click", this.onButtonClick.bind(this));
    this.buttonEl.addEventListener("keydown", this.onButtonKeyDown.bind(this));
    this.contentEl.addEventListener("keydown", this.onMenuKeyDown.bind(this));

    this.rootEl.addEventListener("focusout", this.onFocusOut.bind(this));

    document.addEventListener("click", this.onDocumentClick.bind(this));
    document.addEventListener("keydown", this.onDocumentKeyDown.bind(this));
  }

  toggle(open, { focusButton = false, focusFirst = false } = {}) {
    if (open === this.isOpen) {
      return;
    }

    this.isOpen = open;
    this.buttonEl.setAttribute("aria-expanded", open.toString());

    if (open) {
      this.contentEl.removeAttribute("hidden");
      this.contentEl.classList.add("is-open");
      this.buttonEl.classList.add("is-active");

      this.currentFocusIndex = -1;

      if (focusFirst && this.menuItems.length > 0) {
        this.focusMenuItem(0);
      }
    } else {
      this.contentEl.setAttribute("hidden", "");
      this.contentEl.classList.remove("is-open");
      this.buttonEl.classList.remove("is-active");
      if (focusButton) {
        this.buttonEl.focus();
      }
    }

    this.dispatchEvent(open ? "menuopen" : "menuclose");
  }

  focusMenuItem(index) {
    if (index >= 0 && index < this.menuItems.length) {
      this.currentFocusIndex = index;
      this.menuItems[index].focus();
    }
  }

  focusNextMenuItem() {
    const nextIndex = (this.currentFocusIndex + 1) % this.menuItems.length;
    this.focusMenuItem(nextIndex);
  }

  focusPreviousMenuItem() {
    const prevIndex =
      this.currentFocusIndex <= 0
        ? this.menuItems.length - 1
        : this.currentFocusIndex - 1;
    this.focusMenuItem(prevIndex);
  }

  onButtonClick() {
    this.toggle(!this.isOpen);
  }

  onButtonKeyDown(e) {
    switch (e.key) {
      case "Enter":
      case " ":
      case "ArrowDown":
        e.preventDefault();
        this.toggle(true, { focusFirst: true });
        break;
      case "ArrowUp":
        e.preventDefault();
        this.toggle(true);
        if (this.menuItems.length > 0) {
          this.focusMenuItem(this.menuItems.length - 1);
        }
        break;
      case "Escape":
        if (this.isOpen) {
          e.preventDefault();
          this.toggle(false, { focusButton: true });
        }
        break;
    }
  }

  onMenuKeyDown(e) {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        this.focusNextMenuItem();
        break;
      case "ArrowUp":
        e.preventDefault();
        this.focusPreviousMenuItem();
        break;
      case "Home":
        e.preventDefault();
        this.focusMenuItem(0);
        break;
      case "End":
        e.preventDefault();
        this.focusMenuItem(this.menuItems.length - 1);
        break;
      case "Escape":
        e.preventDefault();
        this.toggle(false, { focusButton: true });
        break;
      case "Tab":
        this.toggle(false);
        break;
    }
  }

  onFocusOut(e) {
    // Close menu when focus moves outside container
    if (!this.rootEl.contains(e.relatedTarget)) {
      this.toggle(false);
    }
  }

  onDocumentClick(e) {
    if (
      this.isOpen &&
      !this.buttonEl.contains(e.target) &&
      !this.contentEl.contains(e.target)
    ) {
      this.toggle(false);
    }
  }

  onDocumentKeyDown(e) {
    if (e.key === "Escape" && this.isOpen) {
      e.stopPropagation();
      this.toggle(false, { focusButton: true });
    }
  }

  dispatchEvent(eventType) {
    const event = new CustomEvent(eventType, {
      detail: { navigationDropdown: this },
      bubbles: true,
    });
    this.buttonEl.dispatchEvent(event);
  }
}

document.addEventListener("DOMContentLoaded", function () {
  const dropdowns = document.querySelectorAll(".a11y-navigation-dropdown");
  dropdowns.forEach((dropdownEl) => {
    new NavigationDropdown(dropdownEl);
  });
});

if (typeof module !== "undefined" && module.exports) {
  module.exports = NavigationDropdown;
}
